import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BsBookmarkFill, BsBookmark } from "react-icons/bs";
import {
  FiArrowRight,
  FiBriefcase,
  FiBookOpen,
} from "react-icons/fi";
import TraineeHeader from "./TraineeHeader";
import Footers from "../Home/Footers";
import { SAVED_TYPES, fetchSavedItems, setSavedItem } from "./SaveButton";
import "../Home/theme.css";
import "./TraineeProfile.css";
import "./SaveButton.css";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "jobs", label: "Jobs" },
  { key: "internships", label: "Internships" },
  { key: "trainings", label: "Trainings" },
];

const EMPTY = { jobs: [], internships: [], trainings: [] };

const joinParts = (parts) => parts.filter(Boolean).join(" · ");

function SavedCard({ badge, title, subtitle, meta, to, linkLabel, onRemove }) {
  const catalogState = { from: "trainee-profile" };
  return (
    <article className="tp-match-card">
      <div className="d-flex justify-content-between align-items-start gap-2">
        <span className="tp-match-badge">{badge}</span>
        <button
          type="button"
          className="als-save-icon is-saved"
          onClick={onRemove}
          aria-label="Remove from wishlist"
          title="Remove from wishlist"
        >
          <BsBookmarkFill />
        </button>
      </div>
      <h3>{title}</h3>
      {subtitle ? <p>{subtitle}</p> : null}
      {meta ? <small>{meta}</small> : null}
      <Link to={to} state={catalogState} className="tp-section-link">
        {linkLabel} <FiArrowRight />
      </Link>
    </article>
  );
}

export default function TraineeSaved() {
  const traineeId = localStorage.getItem("traineeId");
  const [data, setData] = useState(EMPTY);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!traineeId) {
      alert("Trainee ID is missing. Please log in.");
      setLoading(false);
      return;
    }
    fetchSavedItems(traineeId)
      .then(setData)
      .catch((err) => {
        console.error("Error fetching saved items:", err);
        setData(EMPTY);
      })
      .finally(() => setLoading(false));
  }, [traineeId]);

  const remove = useCallback(
    async (listKey, idField, itemType, itemId) => {
      const previous = data;
      setData((prev) => ({
        ...prev,
        [listKey]: prev[listKey].filter(
          (it) => String(it[idField]) !== String(itemId)
        ),
      }));
      try {
        await setSavedItem(traineeId, itemType, itemId, false);
      } catch (err) {
        setData(previous);
        alert(err.response?.data?.error || "Could not remove saved item.");
      }
    },
    [data, traineeId]
  );

  const counts = {
    all: data.jobs.length + data.internships.length + data.trainings.length,
    jobs: data.jobs.length,
    internships: data.internships.length,
    trainings: data.trainings.length,
  };
  const show = (key) => filter === "all" || filter === key;

  return (
    <div className="TraineePage als-trainee-dash">
      <TraineeHeader />

      <section className="tp-hero">
        <div className="tp-hero-inner tp-hero-inner--simple">
          <div className="tp-hero-copy">
            <p className="tp-eyebrow">My Wishlist</p>
            <h1>Jobs, internships &amp; courses you saved</h1>
            <p>Everything on your wishlist, in one place.</p>
          </div>
        </div>
      </section>

      <main className="tp-body">
        <div className="d-flex flex-wrap gap-2 mb-3">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={filter === f.key ? "tp-btn-primary" : "tp-btn-outline"}
              onClick={() => setFilter(f.key)}
            >
              {f.label} ({counts[f.key]})
            </button>
          ))}
        </div>

        {loading ? (
          <section className="tp-panel">
            <p className="text-muted">Loading your wishlist…</p>
          </section>
        ) : counts[filter] === 0 ? (
          <section className="tp-panel">
            <div className="tp-empty">
              <BsBookmark />
              <h3>Your wishlist is empty</h3>
              <p>
                Use the Save button on a job, internship, or course to add it to
                your wishlist.
              </p>
              <div className="d-flex gap-2 flex-wrap justify-content-center">
                <Link to="/jobs" className="tp-btn-outline">
                  Browse jobs
                </Link>
                <Link to="/internships" className="tp-btn-outline">
                  Browse internships
                </Link>
                <Link to="/courses" className="tp-btn-outline">
                  Browse courses
                </Link>
              </div>
            </div>
          </section>
        ) : (
          <>
            {show("jobs") && data.jobs.length > 0 ? (
              <section className="tp-panel">
                <div className="tp-panel-head">
                  <h2>
                    <FiBriefcase /> Saved jobs ({data.jobs.length})
                  </h2>
                </div>
                <div className="tp-match-grid">
                  {data.jobs.map((job) => (
                    <SavedCard
                      key={`job-${job.job_id}`}
                      badge={joinParts(["Job", job.status])}
                      title={job.role || "Role"}
                      subtitle={joinParts([
                        job.company?.company_name,
                        job.location,
                      ])}
                      meta={joinParts([job.salary, job.experience])}
                      to={`/jobs/${job.job_id}`}
                      linkLabel="View job"
                      onRemove={() =>
                        remove("jobs", "job_id", SAVED_TYPES.JOB, job.job_id)
                      }
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {show("internships") && data.internships.length > 0 ? (
              <section className="tp-panel">
                <div className="tp-panel-head">
                  <h2>
                    <FiBriefcase /> Saved internships ({data.internships.length})
                  </h2>
                </div>
                <div className="tp-match-grid">
                  {data.internships.map((item) => (
                    <SavedCard
                      key={`intern-${item.internship_id}`}
                      badge={joinParts(["Internship", item.status])}
                      title={item.role || "Role"}
                      subtitle={joinParts([
                        item.company?.company_name,
                        item.location,
                      ])}
                      meta={joinParts([item.stipend, item.duration])}
                      to={`/internships/${item.internship_id}`}
                      linkLabel="View internship"
                      onRemove={() =>
                        remove(
                          "internships",
                          "internship_id",
                          SAVED_TYPES.INTERNSHIP,
                          item.internship_id
                        )
                      }
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {show("trainings") && data.trainings.length > 0 ? (
              <section className="tp-panel">
                <div className="tp-panel-head">
                  <h2>
                    <FiBookOpen /> Saved courses ({data.trainings.length})
                  </h2>
                </div>
                <div className="tp-match-grid">
                  {data.trainings.map((batch) => (
                    <SavedCard
                      key={`batch-${batch.batch_id}`}
                      badge={joinParts(["Course", batch.mode])}
                      title={
                        batch.course_material?.course?.course_name ||
                        batch.batch_name ||
                        "Course"
                      }
                      subtitle={joinParts([
                        batch.batch_name,
                        batch.venue_address,
                      ])}
                      meta={joinParts([
                        batch.batch_starts_at
                          ? `Starts ${batch.batch_starts_at}`
                          : null,
                        batch.duration,
                        batch.fee != null && batch.fee !== ""
                          ? `₹${batch.fee}`
                          : null,
                      ])}
                      to={`/courses/${batch.batch_id}`}
                      linkLabel="View course"
                      onRemove={() =>
                        remove(
                          "trainings",
                          "batch_id",
                          SAVED_TYPES.TRAINING,
                          batch.batch_id
                        )
                      }
                    />
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}
      </main>

      <Footers />
    </div>
  );
}
